// medication_taken schema + Bedrock tool definition (PLAN.md §6.1 Batch 1, §17.2).
// Pure data only — safe to import from both the API (Lambda) and the web SPA.

import type { OverdoseSeverity } from "./doseSafety.js";

// Dose units offered in the UI and exposed to the capture/onboarding tools.
// Grouped: mass → activity/electrolyte → volume → concentration → countable forms.
export const MED_UNITS = [
  "mg", "mcg", "g",
  "IU", "unit", "mEq",
  "mL", "tsp", "tbsp",
  "%",
  "drop", "spray", "puff", "tablet", "capsule", "patch",
] as const;
export type MedUnit = (typeof MED_UNITS)[number];

/** Discrete, countable dose-form units — a physical "one pill" (D-pillstepper). */
export const MED_FORM_UNITS = ["tablet", "capsule", "patch", "drop", "spray", "puff"] as const;
export type MedFormUnit = (typeof MED_FORM_UNITS)[number];

/** True when `u` is one of the discrete form units (vs. a continuous measure like mg/mL). */
export function isFormUnit(u: MedUnit | undefined): u is MedFormUnit {
  return !!u && (MED_FORM_UNITS as readonly string[]).includes(u);
}

/**
 * Release profile of a medication (D-6, dose-safety). Extended-release products
 * deliberately pack a larger amount into one labeled dose than the immediate-
 * release single-dose ceiling, so the overdose guard must treat one ER tablet as
 * a normal single dose rather than an overdose (MEDICATION_OVERDOSE_PLAN.md §10.1).
 * When unset, callers treat the medication as immediate-release.
 */
export const MED_RELEASES = ["immediate", "extended"] as const;
export type MedRelease = (typeof MED_RELEASES)[number];

/**
 * Heuristic detection of an extended-release product from its name (overdose
 * guard ①). Used as a FALLBACK when the structured `release` field is absent —
 * the capture model sets `release` explicitly (②), but legacy/free-text names
 * still need to be recognized. Matches the common label cues ("24 Hour",
 * "Extended-Release", "ER"/"XR"/"XL"/"SR"/"CR" as standalone tokens). Brand-only
 * ER names with no textual cue (e.g. "OxyContin") are NOT caught here — those rely
 * on the structured field. Returns "extended" or null (never asserts "immediate",
 * so the structured field always wins when present).
 */
const ER_NAME_RE =
  /\b(?:24\s*-?\s*h(?:ou)?rs?|12\s*-?\s*h(?:ou)?rs?|extended[ -]?release|controlled[ -]?release|sustained[ -]?release|modified[ -]?release|ER|XR|XL|SR|CR)\b/i;

export function inferReleaseFromName(name: string | undefined | null): MedRelease | null {
  return typeof name === "string" && ER_NAME_RE.test(name) ? "extended" : null;
}

export const MED_ROUTES = [
  "oral",
  "sublingual",
  "topical",
  "transdermal",
  "inhaled",
  "injected",
  "rectal",
  "ophthalmic",
  "otic",
  "nasal",
] as const;
export type MedRoute = (typeof MED_ROUTES)[number];

// Day-part anchors, ordered by the time of day they usually occur (D-47).
// Used as the medication `scheduledGroup`, the Regimens time-of-day picker,
// and the capture-time reference table. Patients can override the clock time
// of any of these via their preferences (Patient.dayPartTimes).
export const SCHEDULED_GROUPS = [
  "breakfast",
  "morning",
  "noon",
  "lunch",
  "afternoon",
  "dinner",
  "evening",
  "night",
  "bedtime",
  "other",
] as const;
export type ScheduledGroup = (typeof SCHEDULED_GROUPS)[number];

/**
 * Default local "HH:MM" anchor for each day-part. Used when a patient hasn't
 * set a custom time, both as the Regimens default-time hint and as the
 * representative time the capture model assigns when the user names a day-part
 * or meal without a clock time (D-10, D-47).
 */
export const DAY_PART_DEFAULT_TIME: Record<ScheduledGroup, string> = {
  breakfast: "07:30",
  morning: "08:00",
  noon: "12:00",
  lunch: "12:30",
  afternoon: "15:00",
  dinner: "18:00",
  evening: "19:30",
  night: "21:30",
  bedtime: "22:00",
  other: "12:00",
};

/** Human label for a day-part (capitalized). */
export const dayPartLabel = (g: ScheduledGroup): string =>
  g.charAt(0).toUpperCase() + g.slice(1);

/**
 * Merge a patient's custom day-part times over the defaults. Custom values that
 * aren't valid "HH:MM" are ignored. Returns a full map for every day-part.
 */
export function resolveDayPartTimes(
  custom?: Partial<Record<ScheduledGroup, string>> | Record<string, string>
): Record<ScheduledGroup, string> {
  const out = { ...DAY_PART_DEFAULT_TIME };
  if (custom) {
    for (const g of SCHEDULED_GROUPS) {
      const v = (custom as Record<string, string>)[g];
      if (typeof v === "string" && /^\d{2}:\d{2}$/.test(v)) out[g] = v;
    }
  }
  return out;
}

// ── Per-medication dosing schedule (MEDICATION_SCHEDULE_PLAN.md) ──────────────
// An OPTIONAL, user-set schedule on a RegimenItem that drives proactive
// suggestions deterministically — instead of relying on the regimen day-part
// anchor (which assumes daily repetition) or the learned-from-history source.
// Absent ⇒ existing behavior is unchanged. The learned source is NEVER written
// here; this field is only ever set by the user.

export type MedScheduleCadence =
  /** Every day. */
  | { type: "daily" }
  /** Every N days, RELATIVE to the last logged dose (n >= 2). Due once
   *  `daysBetween(lastDoseDay, today) >= n`; resets when a dose is logged. */
  | { type: "everyNDays"; n: number }
  /** On specific local weekdays (0 = Sun … 6 = Sat). */
  | { type: "daysOfWeek"; days: number[] }
  /** Interval dosing: due `hours` after the last dose (1–48). */
  | { type: "everyNHours"; hours: number };

export interface MedSchedule {
  cadence: MedScheduleCadence;
  /**
   * Local "HH:MM" clock times on an on-day. Used by daily / everyNDays /
   * daysOfWeek; ignored for everyNHours. Empty/absent ⇒ fall back to the
   * regimen's day-part time so a cadence-only schedule still works.
   */
  times?: string[];
  /**
   * Server-stamped ISO instant of when this item's `cadence` was last changed
   * (set/carried-forward by saveRegimen; never sent by the client). Absent means
   * "unknown" — treat as if the cadence has always been what it is now. Lets the
   * backfill sweep (api/src/lib/backfill.ts) avoid retroactively judging days that
   * predate a schedule change by the NEW cadence's day-count math
   * (MED_REMINDER_CUSTOM_DATE_CADENCE_FIX_PLAN.md D-CF-1/D-CF-2).
   */
  cadenceSetAt?: string;
}

const MAX_EVERY_N_DAYS = 365;
const MAX_EVERY_N_HOURS = 48;

/** Parse "HH:MM" → true when a valid 24h clock time. */
const isHM = (v: unknown): v is string => {
  if (typeof v !== "string") return false;
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return false;
  return Number(m[1]) <= 23 && Number(m[2]) <= 59;
};

/** Normalize "H:MM" / "HH:MM" to zero-padded "HH:MM". */
const normHM = (v: string): string => {
  const [h, m] = v.trim().split(":");
  return `${h.padStart(2, "0")}:${m}`;
};

/**
 * Coerce arbitrary input (from the regimen save API or the web editor) into a
 * valid MedSchedule, or `undefined` when it can't be made valid. Never throws —
 * an unusable schedule is simply dropped so a bad value can't block a save.
 * Clamps everyNDays to [2, 365] and everyNHours to [1, 48], dedupes/sorts
 * weekdays and times, and discards invalid times.
 */
export function validateMedSchedule(input: unknown): MedSchedule | undefined {
  if (!input || typeof input !== "object") return undefined;
  const raw = input as Record<string, unknown>;
  const c = raw.cadence as Record<string, unknown> | undefined;
  if (!c || typeof c !== "object") return undefined;

  let cadence: MedScheduleCadence;
  switch (c.type) {
    case "daily":
      cadence = { type: "daily" };
      break;
    case "everyNDays": {
      const n = Math.round(Number(c.n));
      if (!Number.isFinite(n)) return undefined;
      cadence = { type: "everyNDays", n: Math.min(Math.max(n, 2), MAX_EVERY_N_DAYS) };
      break;
    }
    case "everyNHours": {
      const hours = Math.round(Number(c.hours));
      if (!Number.isFinite(hours)) return undefined;
      cadence = { type: "everyNHours", hours: Math.min(Math.max(hours, 1), MAX_EVERY_N_HOURS) };
      break;
    }
    case "daysOfWeek": {
      const arr = Array.isArray(c.days) ? c.days : [];
      const days = [...new Set(arr.map((d) => Math.round(Number(d))).filter((d) => d >= 0 && d <= 6))].sort(
        (a, b) => a - b
      );
      if (!days.length) return undefined;
      cadence = { type: "daysOfWeek", days };
      break;
    }
    default:
      return undefined;
  }

  // everyNHours ignores times; the others fall back to the day-part anchor when empty.
  let times: string[] | undefined;
  if (cadence.type !== "everyNHours" && Array.isArray(raw.times)) {
    const clean = [...new Set(raw.times.filter(isHM).map(normHM))].sort();
    if (clean.length) times = clean;
  }

  return times ? { cadence, times } : { cadence };
}

/** One-line human summary of a schedule, e.g. "Every 2 days · 8:00 AM". */
export function formatMedSchedule(s: MedSchedule): string {
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const to12h = (hm: string): string => {
    const [h, m] = hm.split(":").map(Number);
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  };
  let head: string;
  switch (s.cadence.type) {
    case "daily":
      head = "Daily";
      break;
    case "everyNDays":
      head = `Every ${s.cadence.n} days`;
      break;
    case "everyNHours":
      return `Every ${s.cadence.hours} hours`;
    case "daysOfWeek":
      head = s.cadence.days.map((d) => DOW[d]).join(", ");
      break;
  }
  const times = s.times?.length ? ` · ${s.times.map(to12h).join(", ")}` : "";
  return `${head}${times}`;
}

/**
 * One active ingredient of a combination medication (D-6). Used when a product
 * has more than one active ingredient (e.g. Claritin-D = loratadine 10 mg +
 * pseudoephedrine 240 mg) and a single numeric dose cannot represent it.
 */
export interface MedIngredient {
  name: string;
  dose: number;
  unit: MedUnit;
  /** Per-ingredient release profile, when it differs from the product (rare). */
  release?: MedRelease;
  /** RxNorm concept id resolved from this ingredient's `name` (combination
   *  products resolve per-ingredient, since one product has no single rxcui). */
  rxcui?: string;
}

/** Payload for an `medication_taken` event (PLAN.md §6.1, D-6). */
export interface MedicationTakenPayload {
  name: string; // R
  dose?: number; // R for single-ingredient meds; omitted when `ingredients` is set
  unit?: MedUnit; // R for single-ingredient meds; omitted when `ingredients` is set
  ingredients?: MedIngredient[]; // O — combination product breakdown (D-6)
  release?: MedRelease; // O — "extended" for ER/XR/24-hour products (dose-safety §10.1)
  route: MedRoute; // R (defaults to "oral" server-side)
  takenAt: string; // R (auto-now per D-10 if user didn't specify)
  scheduledGroup: ScheduledGroup; // R
  intendedFor?: string; // O
  missed?: boolean; // O
  notes?: string; // O
  /** Set server-side (never by the model) when this dose was flagged by the
   *  overdose guard at capture time (MEDICATION_OVERDOSE_PLAN.md). Absent on
   *  events captured before this field existed — not backfilled retroactively. */
  overdoseSeverity?: OverdoseSeverity;
}

/**
 * Human-readable medication strength. Returns a single dose ("10 mg") for a
 * plain medication, or a "+"-joined ingredient breakdown
 * ("10 mg loratadine + 240 mg pseudoephedrine") for a combination product.
 * Doses of different ingredients are NEVER summed (D-6). `compact` drops the
 * space between number and unit for dense list rows ("10mg loratadine + ...").
 */
export function formatMedStrength(
  m: { dose?: number; unit?: MedUnit; ingredients?: MedIngredient[] },
  compact = false
): string {
  const sep = compact ? "" : " ";
  if (m.ingredients && m.ingredients.length) {
    return m.ingredients.map((i) => `${i.dose}${sep}${i.unit} ${i.name}`).join(" + ");
  }
  if (typeof m.dose === "number" && m.dose > 0) return `${m.dose}${sep}${m.unit ?? ""}`.trim();
  return "";
}

/**
 * Bedrock tool definition (Anthropic Messages tool-use format). The model calls
 * this when the user reports taking a scheduled medication. `takenAt` is
 * intentionally NOT required — omitted means "now" (D-10), filled server-side.
 */
export const RECORD_MEDICATION_TOOL = {
  name: "record_medication_taken",
  description:
    "Record that the user took a medication dose. Required: name. Dose and unit are required for " +
    "single-ingredient medications. scheduledGroup: infer from the current time of day if the user " +
    "does not specify a day-part — do NOT ask. Omit takenAt unless the user stated a specific time.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Medication name as the user said it." },
      dose: {
        type: "number",
        description:
          "Numeric dose amount for a single-ingredient medication. OMIT this and use 'ingredients' " +
          "for a combination product with more than one active ingredient.",
      },
      unit: { type: "string", enum: [...MED_UNITS] },
      ingredients: {
        type: "array",
        description:
          "Active-ingredient breakdown for a COMBINATION product (e.g. Claritin-D = loratadine + " +
          "pseudoephedrine). Provide this INSTEAD of the top-level dose/unit. List each ingredient " +
          "with its own dose and unit. NEVER add the doses of different ingredients together.",
        items: {
          type: "object",
          properties: {
            name: { type: "string", description: "Active ingredient name, e.g. 'loratadine'." },
            dose: { type: "number" },
            unit: { type: "string", enum: [...MED_UNITS] },
          },
          required: ["name", "dose", "unit"],
        },
      },
      release: {
        type: "string",
        enum: [...MED_RELEASES],
        description:
          "Release profile. Set 'extended' when the product is extended/controlled/sustained-release " +
          "or a multi-hour formulation (name says 'ER', 'XR', 'XL', 'SR', 'CR', '12 Hour', '24 Hour', " +
          "or 'extended-release'); otherwise 'immediate'. Omit only if unknown.",
      },
      route: { type: "string", enum: [...MED_ROUTES], description: "Default oral if unstated." },
      takenAt: {
        type: "string",
        description: "ISO-8601 with timezone. Omit if the user did not state a time.",
      },
      scheduledGroup: { type: "string", enum: [...SCHEDULED_GROUPS] },
      intendedFor: { type: "string", description: "What it's for, e.g. 'blood pressure'." },
      missed: { type: "boolean" },
      notes: { type: "string" },
    },
    required: ["name", "scheduledGroup"],
  },
} as const;

/**
 * Tool for registering a medication in the patient's Individual Medications list
 * (kind=as_needed_inventory). Used when the user mentions taking or having a
 * medication that is not yet saved in any regimen or individual medication entry.
 */
export const ADD_INDIVIDUAL_MEDICATION_TOOL = {
  name: "add_individual_medication",
  description:
    "Add a medication to the patient's Individual Medications list. Use this when the user mentions " +
    "taking or having a medication that is NOT already saved in their scheduled regimens or individual " +
    "medications list. Always pair with record_medication_taken when the user is reporting a dose taken now. " +
    "Ask for dose and unit if the user did not provide them.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Medication name." },
      dose: {
        type: "number",
        description:
          "Numeric dose amount for a single-ingredient medication. OMIT this and use 'ingredients' " +
          "for a combination product with more than one active ingredient.",
      },
      unit: { type: "string", enum: [...MED_UNITS], description: "Unit for the dose." },
      ingredients: {
        type: "array",
        description:
          "Active-ingredient breakdown for a COMBINATION product (e.g. Claritin-D = loratadine + " +
          "pseudoephedrine). Provide this INSTEAD of the top-level dose/unit. NEVER add the doses of " +
          "different ingredients together.",
        items: {
          type: "object",
          properties: {
            name: { type: "string", description: "Active ingredient name, e.g. 'loratadine'." },
            dose: { type: "number" },
            unit: { type: "string", enum: [...MED_UNITS] },
          },
          required: ["name", "dose", "unit"],
        },
      },
      release: {
        type: "string",
        enum: [...MED_RELEASES],
        description:
          "Release profile. Set 'extended' for extended/controlled/sustained-release or multi-hour " +
          "products (name says 'ER', 'XR', 'XL', 'SR', 'CR', '12 Hour', '24 Hour', or 'extended-release'); " +
          "otherwise 'immediate'. Omit only if unknown.",
      },
      route: {
        type: "string",
        enum: [...MED_ROUTES],
        description: "Route of administration. Defaults to oral if not stated.",
      },
      intendedFor: {
        type: "string",
        description: "What condition or symptom this medication is taken for (e.g. 'headache', 'blood pressure').",
      },
      instructions: {
        type: "string",
        description: "Usage instructions (e.g. 'take with food', 'as needed for pain').",
      },
      prescribingProvider: {
        type: "string",
        description: "Prescribing provider or doctor, if the user mentioned it.",
      },
    },
    required: ["name"],
  },
} as const;

// ── Medication-clarification types (MEDICATION_CLARIFICATION_PLAN.md) ──────────

/** Which aspect of the medication is ambiguous. */
export type MedVariantDimension = "strength" | "form" | "distinct_drug" | "brand_combo";

/** What the user wants to do with the clarified medication. */
export type MedClarificationIntent = "onboard" | "dose" | "both";

/** A single selectable variant option with all resolved payload fields. */
export interface MedVariantOption {
  /** Human-readable chip label shown to the user, e.g. "10 mg tablet". */
  label: string;
  /** Payload fragment: merged with shared context fields at /chat/clarify-select. */
  payloadFragment: {
    name?: string;
    dose?: number;
    unit?: MedUnit;
    ingredients?: { name: string; dose: number; unit: MedUnit }[];
    release?: MedRelease;
    /** Overrides the context's route — set when a saved-dose variant (e.g. an
     *  inhaler line) has a non-default route of its own (D-thumbsdown-1). */
    route?: MedRoute;
  };
}

/** Context echoed back from the model's tool call — round-tripped through the
 *  client so the click can reconstruct the full proposal with no second model call. */
export interface MedClarificationContext {
  intent: MedClarificationIntent;
  name: string;
  route?: MedRoute;
  takenAt?: string;
  scheduledGroup?: ScheduledGroup;
  intendedFor?: string;
  instructions?: string;
}

/**
 * Tool for requesting user clarification when a medication name is ambiguous.
 * The model calls this instead of add_individual_medication / record_medication_taken
 * when multiple distinct products match what the user said and it would otherwise
 * need to ask "which one?".
 */
export const CLARIFY_MEDICATION_TOOL = {
  name: "clarify_medication",
  description:
    "Request user clarification when a medication name is ambiguous — multiple distinct products " +
    "(different strengths, forms, or ingredients) match what the user said and you cannot " +
    "determine which one without asking. DO NOT call this when the name matches exactly ONE entry in " +
    "the patient's Individual Medications or regimen lists — that's unambiguous. DO call this " +
    "(intent: 'dose') when the SAME name matches TWO OR MORE of the patient's own saved entries at " +
    "different doses — let them pick their saved dose with a button instead of typing it. Only call " +
    "this when you would otherwise have to ask 'which strength?' or 'which product?' in plain text.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Medication name as the user said it." },
      dimension: {
        type: "string",
        enum: ["strength", "form", "distinct_drug", "brand_combo"] as const,
        description:
          "What's ambiguous: 'strength' = dose unclear (10mg vs 20mg); 'form' = tablet/capsule/ER unclear; " +
          "'distinct_drug' = two different drugs share the name (e.g. metoprolol tartrate vs succinate); " +
          "'brand_combo' = brand name could be plain or a combination product (e.g. Tylenol vs Tylenol PM).",
      },
      intent: {
        type: "string",
        enum: ["onboard", "dose", "both"] as const,
        description:
          "'onboard' = save to Individual Medications only; 'dose' = record a dose taken; " +
          "'both' = save AND record a dose (user just mentioned taking it for the first time).",
      },
      prompt: {
        type: "string",
        description: "The clarifying question to show the user, e.g. 'Which lisinopril do you take?'",
      },
      // Shared known fields passed through so the click can reconstruct the proposal.
      route: { type: "string", enum: [...MED_ROUTES] as const },
      takenAt: {
        type: "string",
        description: "ISO-8601 timestamp — only if user stated a specific time.",
      },
      scheduledGroup: { type: "string", enum: [...SCHEDULED_GROUPS] as const },
      intendedFor: { type: "string" },
      instructions: { type: "string" },
    },
    required: ["name", "dimension", "intent", "prompt"],
  },
} as const;
